/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { CalendarDate, EditableDateSegment, IsoMonth } from './types';
import {
  getTodayAsCalendarDate,
  numberToString,
  stringToNumber,
  withinRange
} from '#utils/PRIVATE_dayUtils';
import { getDaysInMonth } from '#utils/UNSAFE_calendarDateUtils';
import { isLeapYear } from '#UNSAFE_IntlDateTime/dateTimeUtils';
import { DateFieldState, DateSegmentState } from './getDateFieldInitialReducerState';

type DirectionType = 'increase' | 'decrease';
type SegmentActionData = Pick<DateSegmentState, 'type'>;
type HomeEndActionData = SegmentActionData & {
  locale: BCP47Locale;
  orderedSegments: EditableDateSegment[];
};
type PageStepActionData = SegmentActionData & {
  direction: DirectionType;
  locale: BCP47Locale;
  orderedSegments: EditableDateSegment[];
  min: number;
  max: number;
};
type UpdateActionData = SegmentActionData & {
  userInput: string;
  hasLeadingZero: boolean;
  locale: BCP47Locale;
  existingText?: string;
  orderedSegments: EditableDateSegment[];
  min: number;
  max: number;
};
type SelectFirstNextPreviousActionData = {
  orderedSegments: EditableDateSegment[];
};
type ClearActionData = {
  orderedSegments: EditableDateSegment[];
};

type DateReducerAction =
  | { actionType: 'clearDate'; data: ClearActionData }
  | {
      actionType: 'clearSegment';
      data: SegmentActionData & ClearActionData;
    }
  | { actionType: 'commitOnBlur'; data?: never }
  | { actionType: 'commitOnEnter'; data?: never }
  | {
      actionType: 'goToEnd';
      data: HomeEndActionData & { max: number };
    }
  | {
      actionType: 'goToHome';
      data: HomeEndActionData & { min: number };
    }
  | {
      actionType: 'page';
      data: PageStepActionData;
    }
  | { actionType: 'selectDate'; data?: never }
  | { actionType: 'selectFirst'; data: SelectFirstNextPreviousActionData }
  | { actionType: 'selectNext'; data: SelectFirstNextPreviousActionData }
  | { actionType: 'selectPrevious'; data: SelectFirstNextPreviousActionData }
  | { actionType: 'selectSegment'; data: SegmentActionData }
  | {
      actionType: 'step';
      data: PageStepActionData;
    }
  | {
      actionType: 'updateSegment';
      data: UpdateActionData;
    }
  | { actionType: 'reset'; data: DateFieldState };

/**
 * Reducer function for updating DateFieldState per the specified action.
 * 'aria-valuenow' is the segment's value. In the dateReducer we look at the 'aria-valuenow'
 * for each segment and when a user updates one segment, that new value may affect the others.
 * In the dateReducer we have a currentDate state that is calculated at the end of the dispatch action
 * - it takes each segment's 'aria-valuenow' and creates the currentDate from it.
 * @param state
 * @param action
 */
const dateReducer = (state: DateFieldState, action: DateReducerAction) => {
  // On mount, the reducer is initialized with initial state derived from the DateField's value.
  // If that value is changed programmatically afterwards, the only way to push that update
  // into the reducer is to define a "reset" action.
  if (action.actionType === 'reset') {
    return action.data;
  }

  switch (action.actionType) {
    case 'clearDate': {
      const updatedSegments = selectFirst(clearDate(state.segments), action.data.orderedSegments);
      const newState = createDateFieldStateWithSelectedDate(updatedSegments, false);
      return newState;
    }
    case 'clearSegment': {
      const updatedSegment = clearSegment(findSegment(state.segments, action.data.type));
      const newState = createDateFieldState(
        state,
        action.data.orderedSegments,
        action.data.type,
        updatedSegment
      );
      return newState;
    }
    case 'commitOnBlur': {
      const updatedSegments = checkFeb29EdgeCase(state.segments);
      const updatedSegmentsClearedAndComplete = clearSelectionAndComplete(updatedSegments);
      const newState = createDateFieldStateWithSelectedDate(
        updatedSegmentsClearedAndComplete,
        false
      );
      return newState;
    }
    case 'commitOnEnter': {
      const updatedSegments = checkFeb29EdgeCase(state.segments);
      const newState = createDateFieldStateWithSelectedDate(updatedSegments, false);
      return newState;
    }
    case 'goToEnd': {
      const segmentState = findSegment(state.segments, action.data.type);
      const max = action.data.max;
      const updatedSegment = goToEnd(action.data.type, segmentState, max);
      const newState = createDateFieldState(
        state,
        action.data.orderedSegments,
        action.data.type,
        updatedSegment,
        false
      );
      return newState;
    }
    case 'goToHome': {
      const segmentState = findSegment(state.segments, action.data.type);
      const min = action.data.min;
      const updatedSegment = goToHome(action.data.type, segmentState, min);
      const newState = createDateFieldState(
        state,
        action.data.orderedSegments,
        action.data.type,
        updatedSegment,
        false
      );
      return newState;
    }
    case 'page': {
      const direction = action.data.direction;
      const segmentState = findSegment(state.segments, action.data.type);
      const min = action.data.min;
      const max = action.data.max;
      const updatedSegment = doStepOrPage(
        action.data.type,
        segmentState,
        'page',
        direction,
        min,
        max
      );
      const newState = createDateFieldState(
        state,
        action.data.orderedSegments,
        action.data.type,
        updatedSegment,
        false
      );
      return newState;
    }
    case 'selectDate': {
      const newState = selectDate(state);
      return newState;
    }
    case 'selectFirst': {
      const updatedSegments = selectFirst(state.segments, action.data.orderedSegments);
      const newState = createDateFieldStateWithSelectedDate(updatedSegments, false);
      return newState;
    }
    case 'selectNext': {
      const updatedSegments = selectNext(state.segments, action.data.orderedSegments);
      const newState = createDateFieldStateWithSelectedDate(updatedSegments, false);
      return newState;
    }
    case 'selectPrevious': {
      const updatedSegments = selectPrevious(state.segments, action.data.orderedSegments);
      const newState = createDateFieldStateWithSelectedDate(updatedSegments, false);
      return newState;
    }
    case 'selectSegment': {
      const updatedSegments = selectSegment(state.segments, action.data.type);
      const newState = createDateFieldStateWithSelectedDate(updatedSegments, false);
      return newState;
    }
    case 'step': {
      const direction = action.data.direction;
      const segmentState = findSegment(state.segments, action.data.type);
      const min = action.data.min;
      const max = action.data.max;
      const updatedSegment = doStepOrPage(
        action.data.type,
        segmentState,
        'step',
        direction,
        min,
        max
      );
      const newState = createDateFieldState(
        state,
        action.data.orderedSegments,
        action.data.type,
        updatedSegment,
        false
      );
      return newState;
    }
    case 'updateSegment': {
      const segmentType = action.data.type;
      const segment = findSegment(state.segments, segmentType);
      const minValue = action.data.min;
      const maxValue = action.data.max;
      const userInput = action.data.userInput;
      const hasLeadingZero = action.data.hasLeadingZero;
      const existingText = action.data.existingText;
      const updatedSegment = updateSegment(
        segmentType,
        segment,
        userInput,
        hasLeadingZero,
        maxValue,
        minValue,
        existingText
      );
      // This passes in the updatedSegment and the rest of the state and figures out if it needs to update other segments.
      // This changes leap year days, for example. Then it takes the updated segments and creates a date field state from it.
      const newState = createDateFieldState(
        state,
        action.data.orderedSegments,
        action.data.type,
        updatedSegment
      );
      return newState;
    }
    default: {
      throw new Error('Unknown action type');
    }
  }
};

const findSegment = (segments: DateSegmentState[], type: EditableDateSegment): DateSegmentState => {
  return segments.find((segment) => segment.type === type)!;
};

// A segment is considered complete if appending a zero to the current value causes it
// to exceed the max. For instance, if you type 2-9 into month or 4-9 into day, the
// segment is complete because you can't type any more digits and have it be a valid value.
const isSegmentComplete = (val: number, max: number) => {
  const num = stringToNumber(numberToString(val) + '0');
  return num > max;
};

// A date is considered a partial date if at least one segment has a value.
const isPartialDate = (allSegments: DateSegmentState[]) => {
  return allSegments.some((seg) => seg['aria-valuenow'] !== undefined);
};

// Construct a calendar date that reflects the values of each segment.
// If none of the segments have a value, return undefined.
const createCalendarDateFromSegments = (segments: DateSegmentState[]) => {
  const hasAnySegmentWithAValue = isPartialDate(segments);
  if (!hasAnySegmentWithAValue) {
    return undefined;
  }

  // This returns something that looks like [ {month: 12}, {day: 25}, {year: 2020} ].
  // Note: we use the aria-valuenow to get the current value as a number (not string).
  const segmentValues = segments.map((seg) => {
    return { [seg.type]: seg['aria-valuenow'] };
  });

  // Iterate over segmentValues and spread each value to get {month: 12, day:25, year: 2020}.
  const iterator = segmentValues.values();
  let cd: CalendarDate = {};
  for (const segValue of iterator) {
    cd = { ...cd, ...segValue };
  }
  return cd;
};

// Find the current value for year.
const getYearValue = (segments: DateSegmentState[]) => {
  const yearState = findSegment(segments, 'year');
  return yearState['aria-valuenow'];
};

// Find the current value for month.
const getMonthValue = (segments: DateSegmentState[]) => {
  const monthState = findSegment(segments, 'month');
  return monthState['aria-valuenow'] as IsoMonth;
};

// Replace the updated segment and return a new DateSegmentState[].
const replaceDateSegment = (
  existingSegments: DateSegmentState[],
  updatedSegmentType: EditableDateSegment,
  updatedSegment: DateSegmentState
) => {
  return existingSegments.map((seg) => (seg.type === updatedSegmentType ? updatedSegment : seg));
};

// Return a new DateSegmentState[] that reflects changes across all segments.
// When you change the month this can affect the day, for instance.
const createDateSegmentStates = (
  existingSegments: DateSegmentState[],
  orderedSegments: EditableDateSegment[],
  updatedSegmentType: EditableDateSegment,
  updatedSegment: DateSegmentState,
  autoAdvance: boolean
) => {
  const modifiedSegments = replaceDateSegment(existingSegments, updatedSegmentType, updatedSegment);
  let updatedSegments = modifiedSegments;

  if (updatedSegmentType === 'month' || updatedSegmentType === 'year') {
    // If month was changed, update the day as needed; this includes adjusting the
    // day value if it now exceeds the max number of days in the month.
    // If year was changed, that can also affect the max number of days (if month is February).
    const daySeg = findSegment(modifiedSegments, 'day');
    if (daySeg) {
      const monthValue =
        updatedSegmentType == 'month'
          ? (updatedSegment['aria-valuenow'] as IsoMonth)
          : getMonthValue(modifiedSegments);
      const yearValue =
        updatedSegmentType === 'year'
          ? updatedSegment['aria-valuenow']
          : getYearValue(modifiedSegments);

      // We will leave dayMax for Feb to 29 until the year is a 4-digit year when the user is typing.
      // JET-75124 Leap year date of 02/29 is converted to 28 automatically (as soon as you type the first number of the year)
      const isFebAndLessThanFourDigitYear =
        monthValue && monthValue === 2 && yearValue && yearValue < 1000;
      // autoAdvance is true when you type and it is false when you spin. If the user is typing into the year,
      // then we want max day to be 29.
      const dayMax =
        isFebAndLessThanFourDigitYear && updatedSegmentType === 'year' && autoAdvance
          ? 29
          : getDaysInMonth(monthValue ?? '*', yearValue ?? '*');
      const currentDay = daySeg['aria-valuenow'];
      const dayValue = !!currentDay && currentDay > dayMax ? dayMax : currentDay;
      const updatedDaySeg = {
        ...daySeg,
        'aria-valuenow': dayValue
      };
      updatedSegments = replaceDateSegment(modifiedSegments, 'day', updatedDaySeg);
    }
  }

  // If the updatedSegment is complete, automatically select the next segment.
  updatedSegments =
    updatedSegment.isComplete && autoAdvance
      ? selectNext(updatedSegments, orderedSegments)
      : updatedSegments;

  return updatedSegments;
};

// Construct a new DateFieldState given an updated segment, which may affect other segments.
const createDateFieldState = (
  currentState: DateFieldState,
  orderedSegments: EditableDateSegment[],
  updatedSegmentType: EditableDateSegment,
  updatedSegment: DateSegmentState,
  autoAdvance = true
): DateFieldState => {
  const updatedSegments = createDateSegmentStates(
    currentState.segments,
    orderedSegments,
    updatedSegmentType,
    updatedSegment,
    autoAdvance
  );

  return createDateFieldStateWithSelectedDate(updatedSegments, currentState.isDateSelected);
};

// Return a new copy of the segment with the value cleared.
const clearSegment = (segmentState: DateSegmentState) => {
  return {
    ...segmentState,
    'aria-valuenow': undefined,
    isComplete: false
  };
};

// Return a new copy of segments with all values cleared.
const clearDate = (segments: DateSegmentState[]) => {
  return segments.map((segmentState) => {
    const cleared = clearSegment(segmentState);
    return { ...cleared };
  });
};

// Return a new copy of the segment with updated values.
const updateSegment = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  userInput: string,
  hasLeadingZero: boolean,
  maxValue: number,
  minValue: number,
  existingText: string | undefined
) => {
  const wasComplete = segmentState.isComplete;

  // If the segment was already complete, replace any existing text with userInput.
  // Otherwise append userInput onto existing text to get the requested string.
  // For instance if the existing text is '1' and userInput (i.e. that the user typed)
  // is '2' then requestedValStr is '12'.
  const requestedValStr =
    wasComplete || existingText === undefined ? userInput : existingText + userInput;
  // If the requested value is out of range, discard the existing text/value and only
  // keep the new one. Check if the segment is complete.
  const requestedVal = stringToNumber(requestedValStr);
  const inRange = withinRange(requestedVal, minValue, maxValue);
  const newVal = inRange ? requestedVal : stringToNumber(userInput);
  const isComplete =
    isSegmentComplete(newVal, maxValue) || (!!hasLeadingZero && segmentType !== 'year');

  // Create a new segment state that reflects the updated value.
  return {
    ...segmentState,
    'aria-valuenow': newVal,
    isComplete
  };
};

// Return a new DateSegmentState[] where the specified segment is selected, all others cleared.
const selectSegment = (segments: DateSegmentState[], segmentType: EditableDateSegment) => {
  const updatedSegments = checkFeb29EdgeCase(segments);
  return updatedSegments.map((seg) => {
    return { ...seg, isSelected: seg.type === segmentType ? true : false };
  });
};

// Return a new DateSegmentState[] where the first segment is selected.
const selectFirst = (segments: DateSegmentState[], orderedSegments: EditableDateSegment[]) => {
  return selectSegment(segments, orderedSegments[0]);
};

// Find the index of the selected segment in the ordered list; return -1 if there is no selection.
const getSelectedSegmentIndex = (
  segments: DateSegmentState[],
  orderedSegments: EditableDateSegment[]
) => {
  const sel = segments.find((seg) => seg.isSelected === true);
  return sel === undefined ? -1 : orderedSegments.findIndex((type) => type === sel.type);
};

// Convenience function to mark the selected segment complete.
const markSelectedSegmentComplete = (segments: DateSegmentState[]) => {
  return segments.map((seg) => (seg.isSelected ? { ...seg, isComplete: true } : seg));
};

// Select the segment following the one that is currently selected.
const selectNext = (segments: DateSegmentState[], orderedSegments: EditableDateSegment[]) => {
  const selectedIndex = getSelectedSegmentIndex(segments, orderedSegments);
  const max = orderedSegments.length - 1;

  // If we're already at the last position, do nothing. Note if nothing was currently selected
  // then selectedIndex is -1. The first segment will be selected.
  return selectedIndex === max
    ? segments
    : selectSegment(markSelectedSegmentComplete(segments), orderedSegments[selectedIndex + 1]);
};

// Select the segment prior to the one that is currently selected.
const selectPrevious = (segments: DateSegmentState[], orderedSegments: EditableDateSegment[]) => {
  const selectedIndex = getSelectedSegmentIndex(segments, orderedSegments);
  return selectedIndex === 0
    ? segments
    : selectedIndex === -1
    ? selectFirst(segments, orderedSegments)
    : selectSegment(markSelectedSegmentComplete(segments), orderedSegments[selectedIndex - 1]);
};

// Return a new DateSegmentState[] where all segments are unselected and any values are
// marked complete. We want to do this when the user commits the DateField value (on blur).
const clearSelectionAndComplete = (segments: DateSegmentState[]) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: false, isComplete: !!seg['aria-valuenow'] };
  });
};

// Return a new DateSegmentState[] where all segments are unselected.
const clearSelection = (segments: DateSegmentState[]) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: false };
  });
};

// Return a new DateFieldState where the entire date is selected.
const selectDate = (currentState: DateFieldState) => {
  return createDateFieldStateWithSelectedDate(clearSelection(currentState.segments), true);
};

// Create a new DateFieldState from the updatedSegments and isDateSelected params.
const createDateFieldStateWithSelectedDate = (
  updatedSegments: DateSegmentState[],
  isDateSelected: boolean
) => {
  return {
    currentDate: createCalendarDateFromSegments(updatedSegments),
    isDateSelected,
    segments: updatedSegments
  };
};

const YEAR_HOME_VALUE = 1900;
const YEAR_END_VALUE = 2100;

// If a segment is empty, use today's date as a starting point for any step or page action.
const getDefaultSegmentValue = (segmentType: EditableDateSegment) => {
  const { year: yearDefault, month: monthDefault, day: dayDefault } = getTodayAsCalendarDate();
  const defaults = { year: yearDefault, month: monthDefault, day: dayDefault };
  return defaults[segmentType];
};

// Get the minimum value for the segment.
const getHomeSegmentValue = (segmentType: EditableDateSegment, min: number) => {
  return segmentType === 'year' ? YEAR_HOME_VALUE : min;
};

// Get the maximum value for the segment (for year, this is YEAR_END_VALUE).
const getEndSegmentValue = (segmentType: EditableDateSegment, max: number) => {
  return segmentType === 'year' ? YEAR_END_VALUE : max;
};

// Return the result of stepping currentValue in the requested direction, taking into account min and max.
const getSteppedSegmentValue = (
  currentValue: number,
  direction: DirectionType,
  min: number,
  max: number
) => {
  const newValue =
    direction === 'increase'
      ? currentValue + 1 > max
        ? min
        : currentValue + 1
      : currentValue - 1 < min
      ? max
      : currentValue - 1;
  return newValue;
};

// Return the result of paging the value up or down, taking into account min and max.
const getPagedSegmentValue = (
  currentValue: number,
  direction: DirectionType,
  min: number,
  max: number,
  segmentType: EditableDateSegment
) => {
  const step = segmentType === 'month' ? 2 : segmentType === 'year' ? 5 : 7;
  const newValue =
    direction === 'increase'
      ? currentValue + step > max
        ? (currentValue + step) % max
        : currentValue + step
      : currentValue - step < min
      ? currentValue - step + max
      : currentValue - step;
  return newValue;
};

// Create a new segment with an updated value from a keyboard action such as step, page, Home, or End.
const createSegmentFromKeyboardAction = (newVal: number, segmentState: DateSegmentState) => {
  // Create a new segment state that reflects the updated value.
  // After any keyboard action, the value is complete so that if the user types anything,
  // it will replace the stepped value.
  return {
    ...segmentState,
    'aria-valuenow': newVal,
    isComplete: true
  };
};

// Set the segment to its minimum value.
const goToHome = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  min: number
) => {
  const newValue = getHomeSegmentValue(segmentType, min);
  return createSegmentFromKeyboardAction(newValue, segmentState);
};

// Set the segment to its maximum value.
const goToEnd = (segmentType: EditableDateSegment, segmentState: DateSegmentState, max: number) => {
  const newValue = getEndSegmentValue(segmentType, max);
  return createSegmentFromKeyboardAction(newValue, segmentState);
};

// Step or page a segment's value.
const doStepOrPage = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  type: 'step' | 'page',
  direction: DirectionType,
  min: number,
  max: number
) => {
  const valueNow = segmentState['aria-valuenow'];

  const newValue =
    valueNow === undefined
      ? getDefaultSegmentValue(segmentType)
      : type === 'page'
      ? getPagedSegmentValue(valueNow, direction, min, max, segmentType)
      : getSteppedSegmentValue(valueNow, direction, min, max);

  return createSegmentFromKeyboardAction(newValue, segmentState);
};

// This is for an edge case where the month is February, the day is 29, and the year is < 1000, and it is not a leap year.
// If so, we update the daySegment to 28. This function is called on 'commit'.
// JET-75124 Leap year date of 02/29 is converted to 28 automatically (as soon as you type the first number in the year)
const checkFeb29EdgeCase = (stateSegments: DateSegmentState[]) => {
  const monthSegment = stateSegments.find((seg) => seg.type === 'month');
  const daySegment = stateSegments.find((seg) => seg.type === 'day');
  const yearSegment = stateSegments.find((seg) => seg.type === 'year');

  const year = yearSegment?.['aria-valuenow'];

  if (
    year !== undefined &&
    year < 1000 &&
    !isLeapYear(year) &&
    monthSegment?.['aria-valuenow'] === 2 &&
    daySegment?.['aria-valuenow'] === 29
  ) {
    const updatedDaySeg = {
      ...daySegment,
      'aria-valuenow': 28
    };
    return replaceDateSegment(stateSegments, 'day', updatedDaySeg);
  }
  return stateSegments;
};

export { dateReducer, numberToString, stringToNumber };
export type { DateReducerAction, DirectionType };
